import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  VpnKey as VpnKeyIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import PageWithHeader from "../common/PageWithHeader";
import LoadingCover from "../common/LoadingCover";
import { useRouter } from "../../hooks/useRouter";
import {
  useGetUsersQuery,
  usePatchUserByIdMutation,
  useDeleteUserByIdMutation,
  usePostUsersAdminCreateMutation,
  usePostUsersAdminResetPasswordByIdMutation,
  User,
} from "../../redux/vmsApi";

// Available roles
const AVAILABLE_ROLES = [
  { value: "user", label: "User", description: "Basic access to the system" },
  { value: "admin", label: "Admin", description: "Full system access" },
  { value: "ceo", label: "CEO", description: "Company-wide dashboard access" },
  { value: "coo", label: "COO", description: "Company-wide dashboard access" },
  {
    value: "c-suite",
    label: "C-Suite",
    description: "Executive team dashboard access",
  },
  {
    value: "agreement",
    label: "Agreements",
    description: "Access to agreements module",
  },
  {
    value: "procurement",
    label: "Procurement",
    description: "Access to procurement module",
  },
  {
    value: "propertyRegister",
    label: "Property Register",
    description: "Access to property register",
  },
  {
    value: "payroll",
    label: "Payroll",
    description: "Access to payroll module",
  },
  {
    value: "staffManagement",
    label: "Staff Management",
    description: "Access to staff management tracker (view own department)",
  },
  {
    value: "Human Resources",
    label: "Human Resources",
    description:
      "Full access to staff management tracker across all departments",
  },
];

// User form interface
interface UserFormData {
  name: string;
  email: string;
  password: string;
  department: string;
  gender: string;
  roles: string[];
}

const initialFormData: UserFormData = {
  name: "",
  email: "",
  password: "",
  department: "",
  gender: "",
  roles: ["user"],
};

const UserManagement: React.FC = () => {
  const router = useRouter();
  const { data: users, isLoading, refetch } = useGetUsersQuery();
  const [patchUser, { isLoading: isPatchLoading }] = usePatchUserByIdMutation();
  const [deleteUser, { isLoading: isDeleteLoading }] = useDeleteUserByIdMutation();
  const [createUser, { isLoading: isCreateLoading }] = usePostUsersAdminCreateMutation();
  const [resetPassword, { isLoading: isResetPasswordLoading }] = usePostUsersAdminResetPasswordByIdMutation();

  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [sortField, setSortField] = useState<"name" | "email" | "department">(
    "name",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [formError, setFormError] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string>("");

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    return users
      .filter((user) => {
        // Search filter
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          !searchTerm ||
          user.name?.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower) ||
          user.department?.toLowerCase().includes(searchLower);

        // Role filter
        const matchesRole =
          roleFilter === "all" || user.roles?.includes(roleFilter);

        // Status filter
        const isActive = user.isActive !== false; // Default to active if not set
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" && isActive) ||
          (statusFilter === "inactive" && !isActive);

        return matchesSearch && matchesRole && matchesStatus;
      })
      .sort((a, b) => {
        const aVal = (a[sortField] || "").toLowerCase();
        const bVal = (b[sortField] || "").toLowerCase();
        if (sortOrder === "asc") {
          return aVal.localeCompare(bVal);
        }
        return bVal.localeCompare(aVal);
      });
  }, [users, searchTerm, roleFilter, statusFilter, sortField, sortOrder]);

  // Handle sort
  const handleSort = (field: "name" | "email" | "department") => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Handle create user
  const handleOpenCreate = () => {
    setFormData(initialFormData);
    setFormError("");
    setShowCreateDialog(true);
  };

  const handleCreateUser = async () => {
    setFormError("");

    // Validate
    if (!formData.name.trim()) {
      setFormError("Name is required");
      return;
    }
    if (!formData.email.trim()) {
      setFormError("Email is required");
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setFormError("Password must be at least 6 characters");
      return;
    }
    if (!formData.gender) {
      setFormError('Gender is required');
      return;
    }

    try {
      await createUser({
        body: {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          department: formData.department || undefined,
          gender: formData.gender,
          roles: formData.roles.length > 0 ? formData.roles : ['user'],
        },
      }).unwrap();
      setShowCreateDialog(false);
      refetch();
    } catch (error: any) {
      setFormError(error?.data?.error?.message || "Failed to create user");
    }
  };

  // Handle edit user
  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || "",
      email: user.email,
      password: "",
      department: user.department || "",
      gender: user.gender || "",
      roles: user.roles || ["user"],
    });
    setFormError("");
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setFormError("");

    try {
      await patchUser({
        id: selectedUser.id!,
        body: {
          name: formData.name,
          department: formData.department || undefined,
          roles: formData.roles,
        },
      }).unwrap();
      setShowEditDialog(false);
      refetch();
    } catch (error: any) {
      setFormError(error?.data?.error?.message || "Failed to update user");
    }
  };

  // Handle delete (deactivate) user
  const handleOpenDelete = (user: User) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser({ id: selectedUser.id! }).unwrap();
      setShowDeleteDialog(false);
      refetch();
    } catch (error: any) {
      console.error("Failed to deactivate user:", error);
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (user: User) => {
    try {
      await patchUser({
        id: user.id!,
        body: {
          isActive: user.isActive === false ? true : false,
        },
      }).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to toggle user status:", error);
    }
  };

  // Handle role change in form
  const handleRoleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      roles: typeof value === "string" ? value.split(",") : value,
    }));
  };

  // Handle password reset
  const handleOpenResetPassword = () => {
    setNewPassword("");
    setFormError("");
    setResetPasswordSuccess("");
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setFormError("");
    setResetPasswordSuccess("");

    if (!newPassword || newPassword.length < 6) {
      setFormError("Password must be at least 6 characters");
      return;
    }

    try {
      await resetPassword({
        id: selectedUser.id!,
        body: { newPassword },
      }).unwrap();
      setResetPasswordSuccess(
        "Password reset successfully. An email has been sent to the user.",
      );
      setNewPassword("");
    } catch (error: any) {
      setFormError(error?.data?.error?.message || "Failed to reset password");
    }
  };

  if (isLoading) return <LoadingCover />;

  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => router.push("/")}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                User Management
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Manage user accounts and permissions
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={() => router.push('/admin/business-units')}
            >
              Business Units
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenCreate}
            >
              Add User
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, email, department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  {AVAILABLE_ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredUsers.length} of {users?.length || 0} users
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Users Table */}
        <Paper>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: "background.paper" }}>
                    <TableSortLabel
                      active={sortField === "name"}
                      direction={sortField === "name" ? sortOrder : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ bgcolor: "background.paper" }}>
                    <TableSortLabel
                      active={sortField === "email"}
                      direction={sortField === "email" ? sortOrder : "asc"}
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ bgcolor: "background.paper" }}>
                    <TableSortLabel
                      active={sortField === "department"}
                      direction={sortField === "department" ? sortOrder : "asc"}
                      onClick={() => handleSort("department")}
                    >
                      Department
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ bgcolor: "background.paper" }}>
                    Roles
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: "background.paper" }}
                    align="center"
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: "background.paper" }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => {
                  const isActive = user.isActive !== false;
                  return (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        opacity: isActive ? 1 : 0.6,
                        bgcolor: isActive ? "inherit" : "action.hover",
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {user.name || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{user.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.department || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          gap={0.5}
                        >
                          {user.roles?.map((role) => (
                            <Chip
                              key={role}
                              label={role}
                              size="small"
                              color={
                                role === "admin"
                                  ? "error"
                                  : role === "ceo" || role === "coo"
                                  ? "warning"
                                  : "default"
                              }
                              variant={role === "user" ? "outlined" : "filled"}
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={isActive ? "Active" : "Inactive"}
                          size="small"
                          color={isActive ? "success" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(user)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              isActive ? "Deactivate User" : "Activate User"
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(user)}
                              color={isActive ? "default" : "success"}
                            >
                              {isActive ? (
                                <CloseIcon fontSize="small" />
                              ) : (
                                <CheckIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Create User Dialog */}
        <Dialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PersonAddIcon />
              <Typography variant="h6">Create New User</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                required
                helperText="Minimum 6 characters"
              />
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    department: e.target.value,
                  }))
                }
              />
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  label="Gender"
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={formData.roles}
                  onChange={handleRoleChange}
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Stack>
                  )}
                >
                  {AVAILABLE_ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Checkbox
                        checked={formData.roles.indexOf(role.value) > -1}
                      />
                      <ListItemText
                        primary={role.label}
                        secondary={role.description}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={isCreateLoading}
              startIcon={
                isCreateLoading ? <CircularProgress size={16} /> : <AddIcon />
              }
            >
              Create User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EditIcon />
              <Typography variant="h6">Edit User</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
              />
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
                helperText="Email cannot be changed"
              />
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    department: e.target.value,
                  }))
                }
              />
              <FormControl fullWidth>
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={formData.roles}
                  onChange={handleRoleChange}
                  input={<OutlinedInput label="Roles" />}
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Stack>
                  )}
                >
                  {AVAILABLE_ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Checkbox
                        checked={formData.roles.indexOf(role.value) > -1}
                      />
                      <ListItemText
                        primary={role.label}
                        secondary={role.description}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button
              color="warning"
              onClick={handleOpenResetPassword}
              startIcon={<VpnKeyIcon />}
            >
              Reset Password
            </Button>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleUpdateUser}
                disabled={isPatchLoading}
                startIcon={
                  isPatchLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CheckIcon />
                  )
                }
              >
                Save Changes
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
        >
          <DialogTitle>Deactivate User</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to deactivate{" "}
              <strong>{selectedUser?.name || selectedUser?.email}</strong>?
            </Alert>
            <Typography variant="body2" color="text.secondary">
              The user will no longer be able to log in, but their data will be
              preserved.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteUser}
              disabled={isDeleteLoading}
              startIcon={
                isDeleteLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <DeleteIcon />
                )
              }
            >
              Deactivate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog
          open={showResetPasswordDialog}
          onClose={() => setShowResetPasswordDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <VpnKeyIcon />
              <Typography variant="h6">Reset Password</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            {resetPasswordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resetPasswordSuccess}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                This will reset the password for{" "}
                <strong>{selectedUser?.name || selectedUser?.email}</strong> and
                send them an email with the new password.
              </Alert>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Minimum 6 characters. The user will receive an email with this password."
                disabled={!!resetPasswordSuccess}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResetPasswordDialog(false)}>
              {resetPasswordSuccess ? "Close" : "Cancel"}
            </Button>
            {!resetPasswordSuccess && (
              <Button
                variant="contained"
                color="warning"
                onClick={handleResetPassword}
                disabled={isResetPasswordLoading || !newPassword}
                startIcon={
                  isResetPasswordLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <VpnKeyIcon />
                  )
                }
              >
                Reset Password
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default UserManagement;
